import { AlertTriangle, Ban, CircleCheck, Loader2, PhoneCall, PhoneMissed, PhoneOff, Voicemail, XCircle, CircleDot } from "lucide-react";
import type {
  CampaignDto,
  CampaignStatsDto,
  CampaignMode,
  CampaignStatus,
  ContactStatus,
  VirtualProvider,
} from "@/shared/api/outboundApi";
import { CAMPAIGN_STATUS_META, CONTACT_STATUS_META, type StatusMeta } from "@/shared/ui/status";

export type Tone = "default" | "success" | "warning" | "danger" | "brand";

/** Gestão = ADMIN/SUPER; leitura = + REPORT (contrato §5). */
export const OUTBOUND_MANAGE_ROLES = ["ROLE_ADMIN", "ROLE_SUPER"];
export const OUTBOUND_READ_ROLES = ["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"];

export const CAMPAIGN_STATUS: Record<CampaignStatus, { label: string; tone: Tone }> = {
  DRAFT: { label: "Rascunho", tone: "default" },
  RUNNING: { label: "Em execução", tone: "success" },
  PAUSED: { label: "Pausada", tone: "warning" },
  FINISHED: { label: "Finalizada", tone: "brand" },
};

export const MODE_LABEL: Record<CampaignMode, string> = {
  HUMAN_PROGRESSIVE: "Humano progressivo",
  VIRTUAL_AGENT: "Agente virtual",
};

export const PROVIDER_LABEL: Record<VirtualProvider, string> = {
  ELEVENLABS_TWILIO: "ElevenLabs (Twilio)",
  ASTERISK_SIP: "SIP via Asterisk",
  MOCK: "Simulação (mock)",
};

export const CONTACT_STATUS: Record<ContactStatus, { label: string; tone: Tone }> = {
  PENDING: { label: "Pendente", tone: "default" },
  SCHEDULED: { label: "Reagendado", tone: "default" },
  DIALING: { label: "Discando", tone: "brand" },
  ANSWERED: { label: "Atendeu", tone: "brand" },
  CONNECTED: { label: "Conectado", tone: "success" },
  COMPLETED: { label: "Concluído", tone: "success" },
  NO_ANSWER: { label: "Não atendeu", tone: "warning" },
  BUSY: { label: "Ocupado", tone: "warning" },
  FAILED: { label: "Falha", tone: "danger" },
  MACHINE: { label: "Caixa postal", tone: "warning" },
  ABANDONED: { label: "Abandonada", tone: "danger" },
  CANCELLED: { label: "Cancelado", tone: "default" },
  DNC: { label: "Não ligar (DNC)", tone: "danger" },
};

export const CONTACT_STATUS_ORDER: ContactStatus[] = [
  "PENDING",
  "SCHEDULED",
  "DIALING",
  "ANSWERED",
  "CONNECTED",
  "COMPLETED",
  "NO_ANSWER",
  "BUSY",
  "MACHINE",
  "ABANDONED",
  "FAILED",
  "CANCELLED",
  "DNC",
];

export const ATTEMPT_RESULT_LABEL: Record<string, string> = {
  ANSWERED: "Atendeu",
  COMPLETED: "Concluída",
  NO_ANSWER: "Não atendeu",
  BUSY: "Ocupado",
  CONGESTION: "Congestionado",
  FAILED: "Falha",
  MACHINE: "Caixa postal",
  ABANDONED: "Abandonada",
};

export const WEBHOOK_STATUS_LABEL: Record<string, string> = {
  PENDING: "Pendente",
  SENT: "Enviado",
  FAILED: "Falhou",
  NONE: "—",
};

export const WEEK_DAYS: { value: string; short: string; label: string }[] = [
  { value: "MON", short: "Seg", label: "Segunda" },
  { value: "TUE", short: "Ter", label: "Terça" },
  { value: "WED", short: "Qua", label: "Quarta" },
  { value: "THU", short: "Qui", label: "Quinta" },
  { value: "FRI", short: "Sex", label: "Sexta" },
  { value: "SAT", short: "Sáb", label: "Sábado" },
  { value: "SUN", short: "Dom", label: "Domingo" },
];

export function contactStatusMeta(status: string | null | undefined) {
  return CONTACT_STATUS[status as ContactStatus] ?? { label: status ?? "—", tone: "default" as Tone };
}

export function campaignStatusMeta(status: string | null | undefined) {
  return CAMPAIGN_STATUS[status as CampaignStatus] ?? { label: status ?? "—", tone: "default" as Tone };
}

export function modeLabel(c: Pick<CampaignDto, "mode" | "virtualProvider">): string {
  const base = MODE_LABEL[c.mode] ?? c.mode;
  if (c.mode === "VIRTUAL_AGENT" && c.virtualProvider) {
    return `${base} · ${PROVIDER_LABEL[c.virtualProvider] ?? c.virtualProvider}`;
  }
  return base;
}

export function formatWindowDays(csv: string | null | undefined): string {
  const set = new Set((csv ?? "").split(",").map((d) => d.trim().toUpperCase()));
  return WEEK_DAYS.filter((d) => set.has(d.value))
    .map((d) => d.short)
    .join(", ");
}

/** Ações permitidas por status (contrato §5). */
export function campaignActions(status: CampaignStatus | undefined) {
  return {
    canStart: status === "DRAFT" || status === "PAUSED",
    canPause: status === "RUNNING",
    canFinish: status === "RUNNING" || status === "PAUSED",
    canDelete: status === "DRAFT" || status === "FINISHED",
    canEdit: status !== "FINISHED",
  };
}

/** Contato pode ser cancelado enquanto não estiver em status final. */
export function canCancelContact(status: ContactStatus): boolean {
  return ["PENDING", "SCHEDULED", "DIALING", "ANSWERED"].includes(status);
}

/** Retry: volta para PENDING — útil em finais "sem sucesso" e cancelados. */
export function canRetryContact(status: ContactStatus): boolean {
  return ["NO_ANSWER", "BUSY", "FAILED", "MACHINE", "ABANDONED", "CANCELLED", "SCHEDULED"].includes(status);
}

/** "5511999990000" → "+55 (11) 99999-0000" (melhor esforço). */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  const d = phone.replace(/\D/g, "");
  if (d.length === 13 && d.startsWith("55")) return `+55 (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12 && d.startsWith("55")) return `+55 (${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return phone;
}

/** Segundos → "m:ss" / "h:mm:ss". */
export function formatSeconds(sec: number | null | undefined): string {
  if (sec == null || Number.isNaN(sec) || sec <= 0) return "0:00";
  const s = Math.round(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${r}` : `${m}:${r}`;
}

/** answerRate pode vir 0..1 ou 0..100. */
export function formatRate(rate: number | null | undefined): string {
  if (rate == null || Number.isNaN(rate)) return "0%";
  const pct = rate <= 1 ? rate * 100 : rate;
  return `${pct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}

/** Slug para integrationKey: minúsculas, sem acento, hífens. */
export function slugify(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export function payloadEntries(payload: unknown): [string, string][] {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return [];
  return Object.entries(payload as Record<string, unknown>).map(([k, v]) => [
    k,
    v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v),
  ]);
}

export type KeyValueRow = { id: number; key: string; value: string };

let kvSeq = 0;
export function newKeyValueRow(key = "", value = ""): KeyValueRow {
  kvSeq += 1;
  return { id: kvSeq, key, value };
}

/** Linhas do editor → objeto payload (ignora chaves vazias). */
export function rowsToPayload(rows: KeyValueRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of rows) {
    const k = r.key.trim();
    if (k) out[k] = r.value;
  }
  return out;
}

/** Só dígitos (o backend normaliza igual; usado para validação na UI). */
export function phoneDigits(phone: string): string {
  return phone.replace(/\D/g, "");
}

export function validatePhone(phone: string): string | undefined {
  const d = phoneDigits(phone);
  if (!d) return "Informe o telefone.";
  if (d.length < 10 || d.length > 15) return "Telefone inválido: use DDD + número (10 a 15 dígitos, com ou sem +55).";
  return undefined;
}

/* ------------------------------------------------- metas visuais (tokens) -- */

/** Status da campanha → meta do StatusBadge (cor semântica + ícone + texto). */
export function campaignStatusUi(status: string | null | undefined): StatusMeta {
  return (
    CAMPAIGN_STATUS_META[status as keyof typeof CAMPAIGN_STATUS_META] ?? {
      label: status ?? "—",
      tone: "neutral",
      icon: CircleDot,
    }
  );
}

/** Status do contato → meta do StatusBadge. */
export function contactStatusUi(status: string | null | undefined): StatusMeta {
  return CONTACT_STATUS_META[status ?? ""] ?? { label: status ?? "—", tone: "neutral", icon: CircleDot };
}

const ATTEMPT_RESULT_META: Record<string, StatusMeta> = {
  ANSWERED: { label: "Atendeu", tone: "inuse", icon: PhoneCall },
  COMPLETED: { label: "Concluída", tone: "success", icon: CircleCheck },
  NO_ANSWER: { label: "Não atendeu", tone: "warning", icon: PhoneMissed },
  BUSY: { label: "Ocupado", tone: "warning", icon: CircleDot },
  CONGESTION: { label: "Congestionado", tone: "danger", icon: AlertTriangle },
  FAILED: { label: "Falha", tone: "danger", icon: XCircle },
  MACHINE: { label: "Caixa postal", tone: "warning", icon: Voicemail },
  ABANDONED: { label: "Abandonada", tone: "danger", icon: PhoneOff },
  DNC: { label: "Não ligar (DNC)", tone: "danger", icon: Ban },
};

/** Resultado de tentativa → meta do StatusBadge (sem resultado = em andamento). */
export function attemptResultUi(result: string | null | undefined): StatusMeta {
  if (!result) return { label: "Em andamento", tone: "dialing", icon: Loader2, pulse: true };
  return ATTEMPT_RESULT_META[result] ?? { label: ATTEMPT_RESULT_LABEL[result] ?? result, tone: "neutral", icon: CircleDot };
}

/** Progresso da base: contatos já resolvidos (fora de pendente/reagendado/em curso) sobre o total. */
export function campaignProgress(s: CampaignStatsDto | null | undefined): { done: number; total: number; pct: number } {
  const total = s?.total ?? 0;
  if (!s || total <= 0) return { done: 0, total, pct: 0 };
  const open = (s.pending ?? 0) + (s.scheduled ?? 0) + (s.dialing ?? 0) + (s.answered ?? 0) + (s.connected ?? 0);
  const done = Math.max(0, total - open);
  return { done, total, pct: Math.min(100, (done / total) * 100) };
}

/** "08:00–20:00" a partir de "08:00:00". */
export function formatWindow(start: string | null | undefined, end: string | null | undefined): string {
  return `${(start ?? "").slice(0, 5)}–${(end ?? "").slice(0, 5)}`;
}

/** Dias da janela com faixas compactas: "Seg–Sáb", "Todos os dias", "Seg, Qua, Sex". */
export function formatWindowDaysCompact(csv: string | null | undefined): string {
  const set = new Set((csv ?? "").split(",").map((d) => d.trim().toUpperCase()));
  const idx = WEEK_DAYS.map((d, i) => (set.has(d.value) ? i : -1)).filter((i) => i >= 0);
  if (!idx.length) return "Nenhum dia";
  if (idx.length === 7) return "Todos os dias";
  const contiguous = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1);
  if (contiguous && idx.length >= 3) return `${WEEK_DAYS[idx[0]].short}–${WEEK_DAYS[idx[idx.length - 1]].short}`;
  return idx.map((i) => WEEK_DAYS[i].short).join(", ");
}
